import { FC, useContext, useEffect, useState } from "react";
import { Formik, Field, Form } from "formik";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  ButtonSize,
  ButtonVariant,
} from "../../../../../components/button/types";
import { ReactComponent as CloseIcon } from "../../../../../assets/close.svg";
import { ReactComponent as Rocket } from "../../../../../assets/rocket2.svg";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import {
  takeActionAndEvaluateBusinessRules,
  UnlockPhaseService,
} from "../../../../../services/unlock.service";
import { useMutation } from "react-query";
import Button from "../../../../../components/button";
import { AuthContext } from "../../../../../contexts/AuthContext";
import { PopupWrapper } from "../../../../../components/popupWrapper";
import rocketAnimation from "./../../../../../assets/rocket-animation.json";
import Lottie from "lottie-react";
import { useNavigate } from "react-router-dom";
import { IContentWithProgress } from "../../../../../@types";
import { useContentAndProgress } from "../../../../../contexts/UserProgressContext";

interface UnlockPhaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  activePhase: any;
}

const UnlockPhaseModal: FC<UnlockPhaseModalProps> = ({
  isOpen,
  onClose,
  activePhase,
}) => {
  const { t } = useTranslation("unlock");
  const { refetchConfigs, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const { milestones, phases, updateUserProgressTree } =
    useContentAndProgress();
  const journeyId = user?.userDetails.journeyId;

  const [showRocket, setShowRocket] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);

  const lastPhase = phases?.[phases.length - 1];
  const phaseNumber =
    (lastPhase?.content?.orderForProgress[journeyId ?? ""] || 0) + 1;

  const submitButton = t("submit").replace(
    "{phaseNumber}",
    phaseNumber.toString()
  );
  const newJobTypes = [
    { key: "employment", value: "employment" },
    { key: "employmentCareerChange", value: "employmentCareerChange" },
    { key: "freelance", value: "freelance" },
    { key: "startup", value: "startup" },
    { key: "other", value: "other" },
  ];

  const submitUnlockPhase = useMutation(UnlockPhaseService, {
    onSuccess: (response) => {
      refetchConfigs();
      setFormSubmitted(true);
      setShowRocket(true);

      takeActionAndEvaluateBusinessRule();
    },
    onError: (error: any) => {
      console.log("Error: ", error);
    },
  });

  const takeActionAndEvaluateBusinessRuleMutation = useMutation(
    takeActionAndEvaluateBusinessRules,
    {
      onSuccess: async () => {
        await updateUserProgressTree();

        setTimeout(() => {
          onClose();
          const firstMilestone = milestones
            ?.filter((ms: IContentWithProgress) =>
              activePhase?.content.childContentIds.includes(
                ms.content.platformContentId
              )
            )
            .sort(
              (ms1: IContentWithProgress, ms2: IContentWithProgress) =>
                ms1.content.orderForProgress[journeyId ?? ""] -
                ms2.content.orderForProgress[journeyId ?? ""]
            )[0];

          navigate(
            `/phase/${activePhase?.content.platformContentId}/milestone/${firstMilestone?.content.platformContentId}`
          );
          setFormSubmitted(false);
          setShowRocket(false);
        }, 2000);
      },
      onError: (error: any) => {
        console.log("Error evaluating business rule: ", error);
        setShowRocket(false);
      },
    }
  );

  // Function to call the `takeActionAndEvaluateBusinessRule` mutation
  const takeActionAndEvaluateBusinessRule = () => {
    takeActionAndEvaluateBusinessRuleMutation.mutate();
  };

  const handleSubmit = async (values: any) => {
    try {
      await submitUnlockPhase.mutate(values);
    } catch (error) {
      console.log("Error: ", error);
    }
  };

  const validationSchema = Yup.object({
    newJobType: Yup.string().required(
      t("validation-required") || "Field required"
    ),
    newJobStartDate: Yup.string().required(
      t("validation-required") || "Field required"
    ),
    newJobTitle: Yup.string().required(
      t("validation-required") || "Field required"
    ),
    newJobComments: Yup.string(),
  });

  useEffect(() => {
    if (!isOpen) {
      setFormSubmitted(false);
      setShowRocket(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    isOpen && (
      <PopupWrapper isOpen={isOpen}>
        <div className="bg-white lg:rounded-md w-full max-h-screen overflow-y-auto lg:w-1/3 xl:w-1/4 p-4 relative">
          <div className="flex justify-end items-center mb-3">
            <CloseIcon onClick={onClose} />
          </div>
          {!formSubmitted ? (
            <>
              <div className="flex flex-col items-center justify-center">
                <div className="text-[32px] leading-[43.71px] font-bold">
                  {lastPhase?.title}
                </div>
                <div className="text-[22px] leading-[30.05px] mb-[22px]">
                  {t("your-restart")}
                </div>
                <div className="mb-[22px]">
                  <Rocket />
                </div>
              </div>

              <Formik
                initialValues={{
                  newJobType: "",
                  newJobStartDate: "",
                  newJobTitle: "",
                  newJobComments: "",
                }}
                validationSchema={validationSchema}
                onSubmit={async (values) => {
                  try {
                    await submitUnlockPhase.mutate(values);
                  } catch (error) {
                    console.log("Error: ", error);
                  }
                }}
              >
                {({ values, handleChange, setFieldValue, isValid, dirty }) => (
                  <Form className="flex flex-col items-start justify-start">
                    {/* Job Type Select */}
                    <div className="mb-4 flex flex-col w-full">
                      <label className="text-base">{t("ill-act-as")}</label>
                      <Field
                        as="select"
                        name="newJobType"
                        className="border rounded-lg px-4 py-3.5 text-base"
                        value={values.newJobType}
                        onChange={handleChange}
                      >
                        <option value="" disabled>
                          {t("ill-act-as-placeholder")}
                        </option>
                        {newJobTypes.map((jobType) => (
                          <option key={jobType.key} value={jobType.value}>
                            {t(`newJobType.${jobType.value}`)}
                          </option>
                        ))}
                      </Field>
                    </div>

                    {/* Date Picker */}
                    <div className="mb-4 flex flex-col w-full">
                      <label className="text-base">{t("on-this-day")}</label>
                      <DatePicker
                        selected={
                          values.newJobStartDate
                            ? new Date(values.newJobStartDate)
                            : null
                        }
                        onChange={(date: any) =>
                          setFieldValue(
                            "newJobStartDate",
                            date?.toISOString().substr(0, 10)
                          )
                        }
                        dateFormat="dd/MM/yyyy"
                        placeholderText={
                          t("on-this-day-placeholder") || "Select a date"
                        }
                        className="w-full h-14 mt-1 border-black rounded-lg"
                      />
                    </div>

                    {/* New Job Title */}
                    <div className="mb-4 flex flex-col w-full">
                      <label className="text-base">{t("my-new-tile")}</label>
                      <Field
                        as="input"
                        type="text"
                        name="newJobTitle"
                        placeholder={t("my-new-tile-placeholder")}
                        className="border rounded-lg px-4 py-3.5 text-base"
                        value={values.newJobTitle}
                        onChange={handleChange}
                      />
                    </div>

                    {/* Comments */}
                    <div className="mb-4 flex flex-col w-full">
                      <label className="text-base">{t("comments")}</label>
                      <Field
                        as="textarea"
                        name="newJobComments"
                        placeholder={t("comments-placeholder")}
                        className="border rounded-lg px-4 py-3.5 text-base"
                        value={values.newJobComments}
                        onChange={handleChange}
                        rows={4}
                      />
                    </div>

                    {/* Submit Button */}
                    <div className="flex flex-col w-full">
                      <Button
                        type="submit"
                        variant={ButtonVariant.PRIMARY}
                        size={ButtonSize.MEDIUM}
                        disabled={!isValid || !dirty}
                      >
                        {submitButton}
                      </Button>
                    </div>
                  </Form>
                )}
              </Formik>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center">
              {/* Rocket animation */}

              <div className="text-[32px] leading-[43.71px] font-bold">
                {activePhase?.title}
              </div>
              <div className="text-[22px] leading-[30.05px] mb-[22px]">
                {t("your-restart")}
              </div>

              {showRocket && (
                <Lottie
                  animationData={rocketAnimation}
                  loop={true}
                  style={{ width: "141px", height: "140px" }}
                />
              )}
              {/* Text after animation */}
              <div className="mt-8 text-center">
                <div className=" mb-4 text-[24px] leading-[43.71px] font-bold">
                  {t("congratulations")}
                </div>
                <div className="text-[16px] leading-[30.05px] mb-4">
                  {t("your-restart-unlocked")}
                </div>
              </div>
            </div>
          )}
        </div>
      </PopupWrapper>
    )
  );
};

export default UnlockPhaseModal;
